import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { getJobDetails, clearJobDetails, postToBoardAction, cancelJobAction, approveJobAction,
  unApproveJobAction, sendJobNotificationAction } from '../../../actions/jobs';
import { UIShowAlert, UIShowConfirm } from '../../../actions/ui';
import { formatPrice } from '../../../helpers/formatData';
import { getEstCost } from '../../../helpers/general';
import { getNowInTimeZone } from '../../../helpers/timezones';
import { isJobNotEditable, getDateTimeShiftFullUi, getCancelJobConfirmationMessage, needToHideStatusBadge,
  getHiredWorkersQuantity } from '../../../helpers/jobs';
import { alertMessages, confirmationMessages, labels } from '../../../constants/text';
import { errorCodes } from '../../../constants/errorCodes';
import { jobStatuses } from '../../../constants/jobs';
import { appPath } from '../../../constants';
import { uiDateFormatShort, dayDateFormat } from '../../../constants/date';
import UsersService from '../../../services/UsersService';
import BackArrow from '../../common/ui/BackArrow';
import Loader from '../../common/ui/Loader';
import SettingsMenu from './SettingsMenu';
import WorkersTab from './WorkersTab';
import JobDetailsTab from './JobDetailsTab';

const pageTabs = {
  workers: {
    id: 'workers',
    ui: 'Workers'
  },
  jobInfo: {
    id: 'jobInfo',
    ui: 'Job Details'
  }
};

class JobDetails extends Component {
  static propTypes = {
    jobDetails: PropTypes.object.isRequired,
    getJobDetails: PropTypes.func.isRequired,
    clearJobDetails: PropTypes.func.isRequired,
    postToBoardAction: PropTypes.func.isRequired,
    approveJobAction: PropTypes.func.isRequired,
    unApproveJobAction: PropTypes.func.isRequired,
    sendJobNotificationAction: PropTypes.func.isRequired,
    UIShowConfirm: PropTypes.func.isRequired,
    UIShowAlert: PropTypes.func.isRequired
  };

  state = {
    activeTab: pageTabs.workers.id,
    estCost: ''
  };

  componentDidMount = () => {
    const { getJobDetails, history } = this.props;
    const jobId = this.props.match.params.id;
    getJobDetails(jobId).then(jobDetailsData => {
      if (!jobDetailsData) {
        history.push(`/${appPath}/jobs`);
      }
      if (history.location.state && history.location.state.switchToWorkers) {
        this.switchTab(pageTabs.workers.id);
      }
      this.calculateEstCost();
    });
  };

  componentWillUnmount () {
    this.props.clearJobDetails();
  }

  switchTab = activeTab => {
    this.setState({ activeTab });
    if (activeTab === pageTabs.workers.id) {
      // we need to recalculate est cost in cases when we switch to workers tab during editing job details
      this.calculateEstCost();
    }
  };

  postJob = (postToBoard, needSwitchToWorkers) => {
    this.props.UIShowConfirm(
      confirmationMessages.postJob,
      () => {
        const { postToBoardAction, jobDetails: { id } } = this.props;
        postToBoardAction([id], postToBoard).then(data => {
          if (data.error && data.error.code === errorCodes.noCConFile) {
            this.props.UIShowAlert(alertMessages.noCConFile[UsersService.getUserRole()]);
          } else {
            if (needSwitchToWorkers) {
              this.switchTab(pageTabs.workers.id);
            }
          }
        });
      },
      { cancelButtonLabel: 'No', proceedButtonLabel: 'Yes' }
    );
  };

  cancelJob = () => {
    const { cancelJobAction, getJobDetails, UIShowConfirm, jobDetails: { id, start_date, status, job_workers } } = this.props;
    const confirmMessageText = getCancelJobConfirmationMessage(
      UsersService.getUserRole(),
      status,
      start_date,
      getHiredWorkersQuantity(job_workers)
    );

    UIShowConfirm(
      confirmMessageText,
      () => cancelJobAction(id, UsersService.getUserRole()).then(() => getJobDetails(id)),
      { cancelButtonLabel: 'No', proceedButtonLabel: 'Yes' }
    );
  };

  approveJob = signatureUrl => {
    const { approveJobAction, getJobDetails, jobDetails: { id } } = this.props;
    return approveJobAction(id, signatureUrl).then(() => getJobDetails(id));
  };

  unApproveJob = () => {
    const { unApproveJobAction, getJobDetails, jobDetails: { id } } = this.props;
    unApproveJobAction(id).then(() => getJobDetails(id));
  };

  sendJobNotification = () => {
    console.log("Clicked send notify")
    const { sendJobNotificationAction, UIShowConfirm, jobDetails: { id } } = this.props;

    UIShowConfirm('Are you sure you want to send notifications for this job?', () =>
      sendJobNotificationAction(id));
  }

  calculateEstCost = (data = {}) => {
    const { job_cost_rate, hours, quantity_of_workers } = data;
    const jobCostRate = _.isUndefined(job_cost_rate) ? this.props.jobDetails.job_cost_rate : job_cost_rate;
    const jobHours = _.isUndefined(hours) ? this.props.jobDetails.hours : hours;
    const quantityOfWorkers = _.isUndefined(quantity_of_workers) ? this.props.jobDetails.quantity_of_workers : quantity_of_workers;
    this.setState({
      estCost: getEstCost(jobCostRate, jobHours, quantityOfWorkers) || ''
    });
  };

  render () {
    const { jobDetails } = this.props;
    const { activeTab, estCost } = this.state;
    const { status, is_post_to_board, quantity_of_workers, event, start_date, end_date, job_workers, skill_label,
      is_approved, total_payment, time_zone_id, invoice_number, invoice_date, invoice_url, icon_url } = jobDetails;
    // today's jobs are current (start_date only counts):
    const isPastJob = start_date && start_date.clone().startOf('day') < getNowInTimeZone(time_zone_id).startOf('day');
    const jobIsNotEditable = isJobNotEditable(status, is_approved);
    const jobDateTimeFullUi = start_date ? getDateTimeShiftFullUi(start_date, end_date, time_zone_id, dayDateFormat) : '';
    const hiredWorkersQuantity = getHiredWorkersQuantity(job_workers);
    const hideStatusBadge = needToHideStatusBadge(isPastJob, status, hiredWorkersQuantity > 0);
    const showNeedApproveLabel = hideStatusBadge && !is_approved;
    return (
      <div className='job-details-wrapper'>
        <div className='top'>
          <div className='header-left'>
            <BackArrow
              history={this.props.history}
              location={this.props.location}
              defaultBackPath={`/${appPath}/jobs`}
            />
            <div
              className='job-image'
              style={{ backgroundImage: `url('${icon_url}')` }}
            />
            <div>
              <h3>{`${skill_label || ''}`}</h3>
              <p className='start-date'>{jobDateTimeFullUi}</p>
              { is_post_to_board && !isPastJob && <div className='header-label post-to-board'>Posted to board</div> }
            </div>
          </div>
          <div>
            <div className='d-flex align-center'>
              <p>{ total_payment || total_payment === 0 ? 'Final Cost' : 'Billing Estimate'}</p>
              <h4 className='price'>{ total_payment || total_payment === 0 ? formatPrice(total_payment) : estCost }</h4>
              { !hideStatusBadge && (
                <div className={`status-btn ${status}`}>{jobStatuses[status] ? jobStatuses[status].ui : ''}</div>
              )}
              { is_approved && hideStatusBadge && (
                <div className={`status-btn approved`}>Approved</div>
              )}
              { !jobIsNotEditable && (
                <div className='settings-wrapper'>
                  <SettingsMenu
                    jobDetails={jobDetails}
                    postJob={this.postJob}
                    cancelJob={this.cancelJob}
                  />
                </div>
              )}
            </div>
            { showNeedApproveLabel && (
              <p className='need-approve-label'>
                <span className='icon-wrapper exclamation-icon'>
                  <FontAwesomeIcon icon={faExclamationCircle} />
                </span>
                {labels.needApproveLabel}
              </p>
            )}
            { (invoice_number || event.invoice_number) && (
              <div className='invoice-info'>
                Invoice Number: <a className='link' target='_blank' href={invoice_url || event.invoice_url}>
                  {invoice_number || event.invoice_number}
                </a>
              </div>
            )}
            { (invoice_date || event.invoice_date) && (
              <div className='invoice-info'>
                Invoice Date: {moment(invoice_date || event.invoice_date).format(uiDateFormatShort)}
              </div>
            )}
            <div className='invoice-info'>
              <button
                onClick={() => this.sendJobNotification()}
                className='button button-primary ml-4'
              >
                Send Notification
              </button>
            </div>
          </div>
        </div>
        <div className='tabs-wrapper'>
          <button
            className={activeTab === pageTabs.workers.id ? 'tab active' : 'tab'}
            onClick={() => this.switchTab(pageTabs.workers.id)}
          >{pageTabs.workers.ui} <span className='quantity-info'>{hiredWorkersQuantity} / {quantity_of_workers}</span>
          </button>
          <button
            className={activeTab === pageTabs.jobInfo.id ? 'tab active' : 'tab'}
            onClick={() => this.switchTab(pageTabs.jobInfo.id)}
          >{pageTabs.jobInfo.ui}</button>
        </div>
        { jobDetails.id ? (
          <Fragment>
            { activeTab === pageTabs.jobInfo.id && (
              <JobDetailsTab
                cancelJob={this.cancelJob}
                calculateEstCost={this.calculateEstCost}
              />
            )}
            {activeTab === pageTabs.workers.id && (
              <main className='main-wrapper-details-blocks'>
                <WorkersTab
                  approveJob={this.approveJob}
                  unApproveJob={this.unApproveJob}
                />
              </main>
            )}
          </Fragment>
        ) : <Loader />}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  jobDetails: state.jobs.jobDetails
});

export default connect(mapStateToProps, {
  getJobDetails,
  clearJobDetails,
  postToBoardAction,
  cancelJobAction,
  approveJobAction,
  unApproveJobAction,
  sendJobNotificationAction,
  UIShowAlert,
  UIShowConfirm
})(JobDetails);



// WEBPACK FOOTER //
// ./src/components/app/jobDetails/index.js